import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import PageHeader from "./layout/PageHeader";
import {
  fetchAipChangesAllForScope,
  shouldUseUnityCompanyClientScope,
} from "../utils/api";
import AipChangeCenterTable from "./AipChangeCenterTable";
import "./AipChangeCenterTable.css";
import { AIP_CHANGE_RANGE_OPTIONS } from "../utils/aipChangeCenterUi";
import "./AipChangeCenterPage.css";

const PAGE_SIZE = 25;
const VALID_RANGES = new Set(AIP_CHANGE_RANGE_OPTIONS.map((o) => o.value));

const AipChangeCenterPage = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [changeData, setChangeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const activeLocationId = useMemo(
    () => String(activeLocation?._id || activeLocation?.id || "").trim(),
    [activeLocation],
  );

  const rangeParam = searchParams.get("range") || "today";
  const changeRange = VALID_RANGES.has(rangeParam) ? rangeParam : "today";

  useEffect(() => {
    setPage(1);
  }, [changeRange, activeLocationId]);

  useEffect(() => {
    if (!activeLocationId) {
      setChangeData(null);
      setError("");
      return;
    }

    let active = true;
    setLoading(true);
    setError("");

    fetchAipChangesAllForScope(activeLocation, locations, changeRange)
      .then((payload) => {
        if (!active) return;
        const mergedResults = payload?.results || [];
        setChangeData({
          ...payload,
          results: mergedResults,
          totalClients: mergedResults.length,
          totalMatching: payload?.totalMatching ?? mergedResults.length,
          unityMerged: shouldUseUnityCompanyClientScope(activeLocation),
        });
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || "Failed to load score changes");
        setChangeData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [activeLocationId, changeRange, activeLocation, locations]);

  const results = changeData?.results || [];
  const totalClients = changeData?.totalClients ?? results.length;
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageRows = results.slice(pageStart, pageStart + PAGE_SIZE);

  const handleRangeChange = (nextRange) => {
    const next = new URLSearchParams(searchParams);
    next.set("range", nextRange);
    setSearchParams(next, { replace: true });
  };

  const handleClientClick = (clientId) => {
    navigate(`/client-management/client/${clientId}/aip-analysis`);
  };

  return (
    <div className="acc-page page-shell">
      <PageHeader
        title="AIP Change Center"
        subtitle="All clients at this location with AIP score change details"
        icon={<FontAwesomeIcon icon={faChartLine} className="h-6 w-6 text-white" />}
        activeLocation={activeLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
      />

      <div className="acc-toolbar">
        <Link to="/dashboard" className="acc-back-link">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Dashboard
        </Link>

        <div className="acc-toolbar-right">
          <label className="acc-range-label" htmlFor="acc-range-select">
            Date range
          </label>
          <select
            id="acc-range-select"
            className="db-range-select"
            value={changeRange}
            onChange={(e) => handleRangeChange(e.target.value)}
            aria-label="Score change date range"
          >
            {AIP_CHANGE_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="acc-card">
        {loading && (
          <div className="db-change-loading">Loading score changes…</div>
        )}

        {!loading && error && (
          <div className="db-change-empty db-change-error">{error}</div>
        )}

        {!loading && !error && !activeLocationId && (
          <div className="db-change-empty">Select a location to view score changes.</div>
        )}

        {!loading && !error && activeLocationId && changeData && results.length === 0 && (
          <div className="db-change-empty">
            {changeData.noHistory
              ? "No AIP score history recorded yet for this location."
              : changeData.noClients
              ? "No clients found for this location."
              : "No AIP score data available for this location yet."}
          </div>
        )}

        {!loading && !error && changeData && results.length > 0 && (
          <>
            <div className="acc-table-meta">
              Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, totalClients)} of{" "}
              {totalClients} client{totalClients === 1 ? "" : "s"}
            </div>

            <div className="acc-table-wrap">
              <AipChangeCenterTable
                rows={pageRows}
                onClientClick={handleClientClick}
                nameClassName="acc-change-name"
              />
            </div>

            {totalPages > 1 && (
              <div className="acc-pagination">
                <button
                  type="button"
                  className="acc-page-btn"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="acc-page-meta">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="acc-page-btn"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AipChangeCenterPage;
